import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '../../components/Layout/AppHeader';
import { BottomNav } from '../../components/BottomNav/BottomNav';
import { useAuth } from '../../context/AuthContext';
import { setPreference, getToken, getUsername } from '../../services/storage';
import { getEffectivePreferences } from '../../services/features';
import { useFeatures } from '../../hooks/useFeatures';
import { getPendingDetails, clearPendingDetails, getPendingCount } from '../../services/db';
import { flushQueueFreshFromClient, resetAllRetriesClient, clearAutoRetryThrottle } from '../../services/offlineSyncClient';
import { telemetryLog } from '../../services/telemetry';
import { isOfflineForUser } from '../../services/networkStatus';
import {
  enablePushNotifications,
  getCurrentSubscription,
  isPushSupported,
  pushAvailabilityReason,
} from '../../services/pushNotifications';
import { useInstallFlow } from '../../hooks/useInstallFlow';
import { InstallInstructions } from '../../components/InstallInstructions/InstallInstructions';
import {
  checkForUpdate,
  subscribeUpdate,
  getUpdateSnapshot,
  type UpdateState,
} from '../../services/updateManager';
import { getThemePref, setThemePref, subscribeTheme, type ThemePref } from '../../services/theme';
import { useTranslation } from 'react-i18next';
import { changeLanguage, getDateLocale, languagesForCountries, type AppLanguage } from '../../i18n';
import './SettingsPage.css';

// Cambio de contraseña: lo hace la web de gestión de usuarios, que pide entrar
// si hace falta. La PWA solo abre la página; no pasa token ni datos.
const CHANGE_PASSWORD_URL = 'https://aldahramanagementusers.azurewebsites.net/Identity/Account/Manage/ChangePassword';

const DELETE_PIN = '20262104';

// El texto de la preferencia de galería debe decir la verdad en cada
// plataforma: iOS comparte a la app Fotos (share sheet); Android y escritorio
// DESCARGAN el fichero (en Android va a Descargas y la galería lo indexa; en
// escritorio a la carpeta de Descargas del navegador). Espejo de la detección
// en EditDetailPage.
const IS_IOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
const IS_ANDROID = /android/i.test(navigator.userAgent);

function getTokenExpiry(): string {
  const token = getToken();
  if (!token) return '—';
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const date = new Date(payload.exp * 1000);
    return date.toLocaleString(getDateLocale());
  } catch { return '—'; }
}

interface ToggleRowProps {
  label: string;
  subtitle: string;
  checked: boolean;
  onChange: (val: boolean) => void;
}
function ToggleRow({ label, subtitle, checked, onChange }: ToggleRowProps) {
  return (
    <div className="settings-toggle-row">
      <div className="settings-toggle-info">
        <span className="settings-toggle-label">{label}</span>
        <span className="settings-toggle-desc">{subtitle}</span>
      </div>
      <label className="toggle-switch">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="toggle-slider" />
      </label>
    </div>
  );
}

export function SettingsPage() {
  const { username, logout, claims } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const features = useFeatures();
  // Mismos idiomas que ofrece el header (ver AppHeader): los de sus países si
  // su perfil los admite, ES/EN para el flujo clásico.
  const availableLanguages: AppLanguage[] = features.extendedLanguages
    ? languagesForCountries(claims.userCountries)
    : ['es', 'en'];
  // Preferencias efectivas: las guardadas, recortadas por el perfil. Un valor
  // heredado en localStorage no puede reactivar algo que el perfil no permite.
  const [prefs, setPrefs] = useState(() => getEffectivePreferences(claims));
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [themePref, setThemePrefState] = useState<ThemePref>(getThemePref);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // El tema puede cambiar desde fuera (p.ej. el sistema en modo auto). Nos
  // suscribimos para refrescar el segmented control si la pref efectiva
  // varía sin pasar por nuestro setter.
  useEffect(() => subscribeTheme((_, pref) => setThemePrefState(pref)), []);

  const handleThemeChange = (pref: ThemePref) => {
    setThemePref(pref);
    setThemePrefState(pref);
  };

  // PIN dialog para "Eliminar"
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');

  const updatePref = (key: 'vgm' | 'delete' | 'location' | 'gallery' | 'addCargo', value: boolean) => {
    setPreference(key, value);
    setPrefs(getEffectivePreferences(claims));
  };

  const handleDeleteToggle = (val: boolean) => {
    if (val) {
      setPinInput('');
      setPinError('');
      setPinDialogOpen(true);
    } else {
      updatePref('delete', false);
    }
  };

  const confirmPin = () => {
    if (pinInput === DELETE_PIN) {
      updatePref('delete', true);
      setPinDialogOpen(false);
    } else {
      setPinError(t('settings.pinError'));
    }
  };

  const checkPending = async () => {
    const items = await getPendingDetails();
    setPendingCount(items.length);
  };

  const [uploading, setUploading] = useState(false);

  const forcePendingUpload = async () => {
    const before = await getPendingCount();
    if (before === 0) {
      alert(t('settings.noPending'));
      return;
    }
    if (isOfflineForUser()) {
      alert(navigator.onLine ? t('settings.offlineManual') : t('settings.offlineNoNet'));
      return;
    }
    setUploading(true);
    telemetryLog('forzar_subida', { elementos: before });
    try {
      // "Forzar subida" completo:
      //  1. resetAllRetriesClient: retries=0 y nextAttemptAt=undefined de TODOS
      //     los items — incluidos los aparcados por tope, los que están en
      //     backoff y los marcados como destino inexistente.
      //  2. clearAutoRetryThrottle: el próximo auto-reset de 30 min vuelve a
      //     estar disponible desde ya.
      //  3. flushQueueFreshFromClient: un flush NUEVO. flushQueueFromClient
      //     devuelve la promesa del flush EN CURSO si lo hay — y con cola
      //     pendiente el poll (~1,5s) casi garantiza que lo hay. Unirse a esa
      //     pasada, que arrancó ANTES del reset y ya decidió saltarse los
      //     aparcados, convertía el botón en un no-op justo cuando más falta
      //     hace.
      //  4. postMessage al SW para que su cola también reintente con los
      //     contadores limpios.
      // El flush es el flujo oficial, el mismo que usa el sync automático
      // (multipart 'photos', MIME image/jpg, fallback base64, dedup por
      // fileName, verificación por lectura…).
      await resetAllRetriesClient();
      clearAutoRetryThrottle();
      navigator.serviceWorker?.ready
        .then((reg) => reg.active?.postMessage({ type: 'FLUSH_PENDING', resetRetries: true }))
        .catch(() => {});
      const stats = await flushQueueFreshFromClient();
      const after = await getPendingCount();
      setPendingCount(after);
      if (stats == null) {
        alert(t('settings.uploadOfflineRetry'));
      } else if (after === 0) {
        alert(t('settings.uploadDone', { count: stats.synced }));
      } else {
        // Si quedaron pendientes pueden ser items con foto que el siguiente
        // ciclo reintentará automáticamente.
        alert(t('settings.uploadPartial', { synced: stats.synced, failed: stats.failed, pending: after }));
      }
    } catch {
      alert(t('settings.queueError'));
    } finally {
      setUploading(false);
    }
  };

  const handleClearPending = async () => {
    if (!confirm(t('settings.confirmClearPending'))) return;
    await clearPendingDetails();
    setPendingCount(0);
  };

  // ── Notificaciones push ────────────────────────────────────────────────
  // Estado del permiso y de la subscripción del navegador. Lo refrescamos al
  // montar y tras cualquier acción del usuario, así el botón refleja el
  // estado real (sin tener que recargar la página).
  type PushUiStatus = 'unsupported' | 'unavailable' | 'denied' | 'subscribed' | 'idle';
  const [pushStatus, setPushStatus] = useState<PushUiStatus>('idle');
  const [pushReason, setPushReason] = useState<string | undefined>(undefined);
  const [pushBusy, setPushBusy] = useState(false);

  const refreshPushStatus = async () => {
    if (!isPushSupported()) { setPushStatus('unsupported'); return; }
    const avail = pushAvailabilityReason();
    if (!avail.ok) { setPushStatus('unavailable'); setPushReason(avail.reason); return; }
    if (Notification.permission === 'denied') { setPushStatus('denied'); return; }
    const sub = await getCurrentSubscription();
    setPushStatus(sub ? 'subscribed' : 'idle');
  };

  useEffect(() => { refreshPushStatus(); }, []);

  const handleEnablePush = async () => {
    setPushBusy(true);
    setPushReason(undefined);
    const r = await enablePushNotifications();
    setPushBusy(false);
    if (!r.ok) setPushReason(r.reason);
    await refreshPushStatus();
  };

  const handleDisablePush = async () => {
    if (!confirm(t('settings.confirmDisablePush'))) return;
    setPushBusy(true);
    try {
      const sub = await getCurrentSubscription();
      if (sub) await sub.unsubscribe();
    } catch { /* nada accionable */ }
    setPushBusy(false);
    await refreshPushStatus();
  };


  // ── Instalación de la PWA ──────────────────────────────────────────────
  // Flujo unificado (mismo que el login y el banner): nativo si el navegador
  // puede, si no modal de instrucciones.
  const install = useInstallFlow();

  // ── Buscar actualizaciones ─────────────────────────────────────────────
  const [updateState, setUpdateState] = useState<UpdateState>(getUpdateSnapshot());
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const [updateMessage, setUpdateMessage] = useState<{ kind: 'ok' | 'warn'; text: string } | null>(null);

  useEffect(() => {
    setUpdateState(getUpdateSnapshot());
    return subscribeUpdate(setUpdateState);
  }, []);

  const handleCheckUpdate = async () => {
    setCheckingUpdate(true);
    setUpdateMessage(null);
    const result = await checkForUpdate();
    setCheckingUpdate(false);
    if (result === 'has-update') {
      // Si no hay bloqueos ni cola pendiente, el manager ya disparó el
      // reload (verás el toast). Si hay bloqueos, lo informamos.
      const snap = getUpdateSnapshot();
      if (snap.blockedReasons.length > 0) {
        setUpdateMessage({ kind: 'ok', text: t('settings.updateReady') });
      } else {
        setUpdateMessage({ kind: 'ok', text: t('settings.updateApplying') });
      }
    } else if (result === 'up-to-date') {
      setUpdateMessage({ kind: 'ok', text: t('settings.upToDate', { version: __APP_VERSION__ }) });
    } else {
      // 'check-failed': el fetch a build-info.json falló (sin red).
      setUpdateMessage({ kind: 'warn', text: t('settings.updateCheckFailed') });
    }
  };

  const formatLastCheck = (ts: number | null): string => {
    if (!ts) return t('settings.neverChecked');
    return new Date(ts).toLocaleString(getDateLocale(), {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const handleLogout = async () => {
    if (!confirm(t('settings.confirmLogout'))) return;
    // El aviso al back + baja de push (best-effort) y el overlay "Cerrando
    // sesión" viven dentro de logout() (AuthContext). Esperamos a que termine
    // para navegar a /login.
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="settings-page">
      <AppHeader title={t('settings.title')} showLogo />

      <div className="settings-content">

        {/* Sesión actual */}
        <div className="settings-card settings-card--session">
          <div className="settings-session-header">
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z"/>
            </svg>
            <span className="settings-card__title">{t('settings.session')}</span>
          </div>
          <div className="settings-session-info">
            <div className="settings-session-row">
              <span className="settings-session-label">{t('settings.user')}</span>
              <span className="settings-session-value">{username ?? getUsername() ?? '—'}</span>
            </div>
            <div className="settings-session-row">
              <span className="settings-session-label">{t('settings.expiry')}</span>
              <span className="settings-session-value">{getTokenExpiry()}</span>
            </div>
          </div>
          {/* Un enlace y no window.open: funciona igual con la app instalada
              (standalone) en iOS y Android. */}
          <a
            className="settings-btn settings-btn--logout settings-btn--link"
            href={CHANGE_PASSWORD_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
            </svg>
            {t('settings.changePassword')}
          </a>
          <button className="settings-btn settings-btn--logout" onClick={handleLogout}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
            </svg>
            {t('settings.logout')}
          </button>
        </div>

        {/* Apariencia (tema claro / oscuro / auto) */}
        <div className="settings-card">
          <h3 className="settings-card__title">{t('settings.appearance')}</h3>
          <p className="settings-card__desc">
            {t('settings.appearanceDesc')}
          </p>
          <div className="theme-segmented" role="radiogroup" aria-label="Tema">
            {(['light', 'dark', 'auto'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                role="radio"
                aria-checked={themePref === opt}
                className={`theme-segmented__btn${themePref === opt ? ' theme-segmented__btn--active' : ''}`}
                onClick={() => handleThemeChange(opt)}
              >
                {opt === 'light' && (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M12 7a5 5 0 100 10 5 5 0 000-10zm0-5a1 1 0 011 1v2a1 1 0 11-2 0V3a1 1 0 011-1zm0 17a1 1 0 011 1v2a1 1 0 11-2 0v-2a1 1 0 011-1zM3 11h2a1 1 0 110 2H3a1 1 0 110-2zm16 0h2a1 1 0 110 2h-2a1 1 0 110-2zM5.6 4.2l1.4 1.4a1 1 0 11-1.4 1.4L4.2 5.6a1 1 0 011.4-1.4zm12.8 12.8l1.4 1.4a1 1 0 11-1.4 1.4l-1.4-1.4a1 1 0 011.4-1.4zM4.2 18.4l1.4-1.4a1 1 0 111.4 1.4l-1.4 1.4a1 1 0 01-1.4-1.4zM17 5.6l1.4-1.4a1 1 0 111.4 1.4l-1.4 1.4a1 1 0 01-1.4-1.4z" />
                  </svg>
                )}
                {opt === 'dark' && (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M12.34 2.02A10 10 0 0022 12a10 10 0 11-9.66-9.98zM10 4.5A8 8 0 1019.5 14 6.5 6.5 0 0110 4.5z" />
                  </svg>
                )}
                {opt === 'auto' && (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 2v16a8 8 0 010-16z" />
                  </svg>
                )}
                <span>{opt === 'light' ? t('settings.themeLight') : opt === 'dark' ? t('settings.themeDark') : t('settings.themeAuto')}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Idioma */}
        <div className="settings-card">
          <h3 className="settings-card__title">{t('settings.language.title')}</h3>
          <p className="settings-card__desc">{t('settings.language.desc')}</p>
          <div className="theme-segmented" role="radiogroup" aria-label={t('settings.language.title')}>
            {availableLanguages.map((lng: AppLanguage) => (
              <button
                key={lng}
                type="button"
                role="radio"
                aria-checked={i18n.language === lng}
                className={`theme-segmented__btn${i18n.language === lng ? ' theme-segmented__btn--active' : ''}`}
                onClick={() => changeLanguage(username, lng)}
              >
                <span>{t(`settings.language.${lng}`)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Instalación de la app */}
        <div className="settings-card">
          <h3 className="settings-card__title">{t('settings.install')}</h3>
          {install.hidden ? (
            <>
              <p className="settings-card__desc">{t('settings.installedDesc')}</p>
              <button className="settings-btn settings-btn--outline" disabled style={{ opacity: 0.6 }}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
                {t('settings.installed')}
              </button>
            </>
          ) : (
            <>
              <p className="settings-card__desc">{t('settings.installDesc')}</p>
              <button className="settings-btn settings-btn--primary" onClick={install.trigger}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                </svg>
                {t('settings.installNow')}
              </button>
            </>
          )}
        </div>

        <InstallInstructions open={install.modalOpen} onClose={install.closeModal} />

        {/* Toggles de funcionalidades */}
        <div className="settings-card">
          <ToggleRow
            label={t(IS_IOS ? 'settings.galleryLabel' : IS_ANDROID ? 'settings.galleryLabelAndroid' : 'settings.galleryLabelDesktop')}
            subtitle={t(IS_IOS ? 'settings.gallerySub' : IS_ANDROID ? 'settings.gallerySubAndroid' : 'settings.gallerySubDesktop')}
            checked={prefs.galleryEnabled}
            onChange={(v) => updatePref('gallery', v)}
          />
          {/* VGM, Lot/location y borrado no aplican al perfil self-managed
              (ver services/features.ts). Para el flujo clásico, todo igual. */}
          {features.vgm && (
            <ToggleRow
              label={t('settings.vgmLabel')}
              subtitle={t('settings.vgmSub')}
              checked={prefs.vgmEnabled}
              onChange={(v) => updatePref('vgm', v)}
            />
          )}
          {features.detailLocation && (
            <ToggleRow
              label={t('settings.locationLabel')}
              subtitle={t('settings.locationSub')}
              checked={prefs.locationEnabled}
              onChange={(v) => updatePref('location', v)}
            />
          )}
          {features.addCargoConfigurable && (
            <ToggleRow
              label={t('settings.addCargoLabel')}
              subtitle={t('settings.addCargoSub')}
              checked={prefs.addCargoEnabled}
              onChange={(v) => updatePref('addCargo', v)}
            />
          )}
          {features.deleteCargo && (
            <ToggleRow
              label={t('settings.deleteLabel')}
              subtitle={t('settings.deleteSub')}
              checked={prefs.deleteEnabled}
              onChange={handleDeleteToggle}
            />
          )}
        </div>

        {/* Notificaciones push */}
        <div className="settings-card">
          <h3 className="settings-card__title">{t('settings.notifications')}</h3>
          <p className="settings-card__desc">
            {t('settings.notificationsDesc')}
          </p>
          {pushStatus === 'unsupported' && (
            <p className="settings-card__desc" style={{ color: 'var(--warning-text)' }}>
              {t('settings.pushUnsupported')}
            </p>
          )}
          {pushStatus === 'unavailable' && (
            <p className="settings-card__desc" style={{ color: 'var(--warning-text)' }}>
              {pushReason}
            </p>
          )}
          {pushStatus === 'denied' && (
            <p className="settings-card__desc" style={{ color: 'var(--warning-text)' }}>
              {t('settings.pushDenied')}
            </p>
          )}
          {pushStatus === 'subscribed' && (
            <div className="settings-pending-row" style={{ marginTop: 4 }}>
              <span className="pending-count">{t('settings.pushActive')}</span>
              <button
                className="settings-btn settings-btn--outline"
                onClick={handleDisablePush}
                disabled={pushBusy}
              >
                {t('settings.disable')}
              </button>
            </div>
          )}
          {pushStatus === 'idle' && (
            <div className="settings-pending-row" style={{ marginTop: 4 }}>
              <button
                className="settings-btn settings-btn--primary"
                onClick={handleEnablePush}
                disabled={pushBusy}
              >
                {pushBusy ? t('settings.enabling') : t('settings.enablePush')}
              </button>
            </div>
          )}
          {pushStatus !== 'subscribed' && pushReason && pushStatus !== 'unavailable' && (
            <p className="settings-card__desc" style={{ color: 'var(--danger-strong)' }}>
              {pushReason}
            </p>
          )}
        </div>

        {/* Pendiente por subir */}
        <div className="settings-card">
          <h3 className="settings-card__title">{t('settings.pending')}</h3>
          <p className="settings-card__desc">
            {t('settings.pendingDesc')}
          </p>
          <div className="settings-pending-row">
            {pendingCount !== null && (
              <span className={`pending-count ${pendingCount > 0 ? 'pending-count--warn' : ''}`}>
                {t('settings.pendingCount', { count: pendingCount })}
              </span>
            )}
            <button
              className="settings-btn settings-btn--outline"
              onClick={checkPending}
            >
              {t('settings.viewPending')}
            </button>
            <button
              className="settings-btn settings-btn--primary"
              onClick={forcePendingUpload}
              disabled={uploading || pendingCount === 0}
              style={{ opacity: (uploading || pendingCount === 0) ? 0.55 : 1 }}
            >
              {uploading ? (
                <div className="spinner spinner--small spinner--white" style={{ width: 16, height: 16 }} />
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M19.35 10.04A7.49 7.49 0 0 0 12 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 0 0 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
                </svg>
              )}
              {uploading ? t('settings.uploading') : t('settings.forceUpload')}
            </button>
          </div>
          {pendingCount != null && pendingCount > 0 && (
            <button className="settings-btn settings-btn--danger" onClick={handleClearPending}>
              {t('settings.clearPending')}
            </button>
          )}
        </div>

        {/* Buscar actualizaciones */}
        <div className="settings-card">
          <h3 className="settings-card__title">{t('settings.version')}</h3>
          <p className="settings-card__desc">
            {t('settings.versionDesc')}
          </p>
          <div className="settings-pending-row">
            <span className="pending-count">v{__APP_VERSION__}</span>
            <button
              className="settings-btn settings-btn--primary"
              onClick={handleCheckUpdate}
              disabled={checkingUpdate}
              style={{ opacity: checkingUpdate ? 0.6 : 1 }}
            >
              {checkingUpdate ? (
                <div className="spinner spinner--small spinner--white" style={{ width: 16, height: 16 }} />
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                </svg>
              )}
              {checkingUpdate ? t('settings.checking') : t('settings.checkUpdates')}
            </button>
          </div>
          {updateMessage && (
            <p
              className="settings-card__desc"
              style={{ color: updateMessage.kind === 'warn' ? 'var(--warning-text)' : 'var(--brand-primary)' }}
            >
              {updateMessage.text}
            </p>
          )}
          {updateState.hasUpdate && updateState.blockedReasons.length > 0 && !updateMessage && (
            <p className="settings-card__desc" style={{ color: 'var(--brand-primary)' }}>
              {t('settings.updatePending')}
            </p>
          )}
          <p className="settings-card__desc" style={{ fontSize: 11, color: 'var(--text-softer)', marginTop: 2 }}>
            {t('settings.lastCheck', { value: formatLastCheck(updateState.lastCheckAt) })}
          </p>
        </div>

        <p className="settings-footer">v{__APP_VERSION__}</p>
        <a
          href="https://portal.drpinocode.com/privacy-policy-aldahra-europe"
          target="_blank"
          rel="noreferrer noopener"
          className="settings-footer"
          style={{ textDecoration: 'none' }}
        >
          {t('settings.privacy')}
        </a>
      </div>

      {/* Dialog PIN para eliminar */}
      {pinDialogOpen && (
        <div className="pin-overlay" onClick={() => setPinDialogOpen(false)}>
          <div className="pin-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="pin-dialog__title">{t('settings.pinTitle')}</h3>
            <p className="pin-dialog__desc">{t('settings.pinDesc')}</p>
            <input
              className="pin-dialog__input"
              type="password"
              inputMode="numeric"
              placeholder="••••••••"
              value={pinInput}
              onChange={(e) => { setPinInput(e.target.value); setPinError(''); }}
              autoFocus
            />
            {pinError && <span className="pin-dialog__error">{pinError}</span>}
            <div className="pin-dialog__actions">
              <button className="btn btn--secondary" onClick={() => setPinDialogOpen(false)}>{t('settings.cancel')}</button>
              <button className="btn btn--primary" onClick={confirmPin}>{t('settings.confirm')}</button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
